import { createSelector } from "@reduxjs/toolkit";
import { IRootState } from "../app/store";
import {
  selectHttpDevicesInRoom,
  selectHttpGetMultiplacerDeviceTypesData,
  selectNonPublishedDevicesAsPixPolygons,
} from "./devices/deviceSlice";
import { IPixPolygon } from "../components/PixBimViewer/types";
import {
  convertDevicesToPixPolygons,
  convertSingleDeviceToPixPolygon,
  getViewerPathAirTerminalDirectionsFromDevices,
  viewerDeviceHasValidPolygons,
} from "./devices/deviceUtils";
import { filterAndTransformMcBiItemsToPixPolygons } from "./designUtils";
import {
  selectHttpGetMcBimArkItems,
  selectHttpMcBimCeilingItems,
  selectHttpMcbimSpacesResult,
} from "../McBim/mcbimSelectors";
import { selectHttpDevices } from "./devices/deviceSelectors";
import {
  selectCeilingGridPixPaths,
  selectCompCodePath,
  selectMcBimSpacesPixPolygons,
} from "./designSlice";
import { PixBimViewerAction } from "../components/PixBimViewer/PixBimViewer";
import { generateRandomString, getSingleCalculatedCeilingGrid } from "../utils";
import { isTileInCeilingGridValid } from "../RoomSideBar/SpaceValidations/spaceValidationsUtils";
import { ICeilingGrid, ITile } from "../snapToGrid/snapToGrid";
import { selectedSpaceNumber } from "./designSelectors";
import { selectHttpGetRoomsCurrentFloorResult } from "../Room/roomSelectors";

const selectDeviceOnTheMove = (state: IRootState) => state.design.deviceOnTheMove;
const selectActiveSpaceValidationSpecs = (state: IRootState) =>
  state.design.activeSpaceValidationSpecs;
export const selectValidationTilesPixPolygon = createSelector(
  [
    selectDeviceOnTheMove,
    selectedSpaceNumber,
    selectActiveSpaceValidationSpecs,
    selectHttpGetRoomsCurrentFloorResult,
    selectHttpMcbimSpacesResult,
    selectHttpDevicesInRoom,
    selectCompCodePath,
  ],
  (
    deviceIdOnTheMove,
    spaceNumber,
    spaceValidationSpecs,
    rooms,
    spaces,
    devicesInRoom,
    compCodePath
    // spacesAndRoomsAndDevicesAndProjectNo
  ): IPixPolygon[] => {
    try {
      if (spaceNumber == null || deviceIdOnTheMove === undefined) {
        return [];
      }
      const room = rooms[spaceNumber];
      if (!room) return [];
      const mcbimSpace = spaces.find((space) => space.name === spaceNumber);
      if (!mcbimSpace) return [];

      const ceilingGrid =
        mcbimSpace.polygon != null && room?.ceilingCoordinate != null
          ? getSingleCalculatedCeilingGrid({
              spacePolygon: mcbimSpace.polygon,
              ceilingCoordinate: room.ceilingCoordinate,
            })
          : undefined;

      const allTiles: ITile[] = ceilingGrid?.tiles?.flatMap((row) => row) ?? [];

      const validColor = "#8dca8d";
      const invalidColor = "#dc4242";

      const pixPolygons: IPixPolygon[] = allTiles
        .filter((tile) => {
          // Först: tile måste ha boundingBox med exakt 5 punkter
          if (!tile.boundingBox || tile.boundingBox.length !== 5) {
            return false;
          }

          // Kontrollera att minst en punkt (eller alla) är giltiga
          // Här räcker det med att INTE vara [null, null] eller ogiltiga värden
          const isValidPoint = (point: unknown): point is [number, number] => {
            if (!Array.isArray(point) || point.length !== 2) return false;

            const [x, y] = point;
            return (
              typeof x === "number" &&
              typeof y === "number" &&
              Number.isFinite(x) &&
              Number.isFinite(y)
            );
          };

          const cleanedBoundingBox = tile.boundingBox.filter(isValidPoint);

          // Om du kräver exakt 4 hörn (eller minst 2 osv)
          const allPointsValid = cleanedBoundingBox.length === tile.boundingBox.length;

          // Bara acceptera tiles där ALLA punkter är giltiga
          return allPointsValid;
        })
        .map((tile) => {
          const isTileValid = isTileInCeilingGridValid({
            devicesInRoom: devicesInRoom,
            deviceOnTheMoveId: deviceIdOnTheMove,
            ceilingGrid: ceilingGrid as ICeilingGrid,
            tile: tile,
            room: room,
            space: mcbimSpace,
            mcbimItemsInStandardSpace: [],
            activeSpaceValidationSpecs: spaceValidationSpecs,
            compCodePath: compCodePath as string,
          });
          const pixPolygon: IPixPolygon = {
            id: generateRandomString(5),
            points: tile.boundingBox,
            fillColor: isTileValid ? validColor : invalidColor,
            strokeColor: "#dc4242",
            strokeWidth: "1",
            text: {
              show: false,
              values: [],
              color: "#000",
              fontSize: "12px",
            },
            tooltip: "",
            isMoveable: false,
            isDragSelectable: false,
          };
          return pixPolygon;
        });

      const anyTileValid = pixPolygons.some((tile) => tile.fillColor === validColor);
      if (anyTileValid) {
        return pixPolygons;
      } else {
        return [];
      }
    } catch (error) {
      return [];
    }
  }
);
const selectedDeviceId = (s: IRootState) => s.device.selectedDeviceId;
const selectHideDevices = (s: IRootState) => s.design.hideDevices;
const selectTrueSize = (s: IRootState) => s.design.showTrueSizeOfDevices;
const selectDragSelectSpaces = (s: IRootState) => s.design.dragSelectSpaces;
const selectMeasurementToolActive = (s: IRootState) => s.design.measurementToolActive;

const isNotNull = <T>(v: T | null | undefined): v is T => v != null;

export const selectMultiplacerDevicesPixPolygons = createSelector(
  [
    selectHttpDevices,
    selectHttpGetRoomsCurrentFloorResult,
    selectedDeviceId,
    selectHttpGetMultiplacerDeviceTypesData,
    selectHideDevices,
    selectTrueSize,
    selectDragSelectSpaces,
  ],
  (
    devicesResp,
    roomsResp,
    selectedDeviceId,
    typeDevicesResp,
    hideDevices,
    showTrueSize,
    dragSelectSpaces
  ): IPixPolygon[] => {
    if (dragSelectSpaces) {
      return [];
    }

    if (devicesResp == null || devicesResp.length === 0 || hideDevices) {
      return [];
    }

    return devicesResp
      .map((deviceItem) =>
        convertSingleDeviceToPixPolygon({
          deviceItem,
          typeDevices: typeDevicesResp,
          selectedDeviceId,
          showTrueSize,
          room: roomsResp?.[deviceItem.roomNumber],
          // Only the selected device is moveable
          isMoveable: selectedDeviceId != null && deviceItem._id === selectedDeviceId,
        })
      )
      .filter(isNotNull)
      .filter((pixPolygon) => viewerDeviceHasValidPolygons(pixPolygon));
  }
);

const selectDeviceMode = (s: IRootState) => s.design.deviceViewMode;
export const selectPixPolygonsToShow = createSelector(
  [selectMultiplacerDevicesPixPolygons, selectNonPublishedDevicesAsPixPolygons, selectDeviceMode],
  (multiPlacerDevices, nonPublishedDevices, deviceMode) => {
    if (deviceMode === "devices") {
      return [...multiPlacerDevices, ...nonPublishedDevices];
    }
  }
);

const selectInstanceClassesToDisplay = (state: IRootState) => state.design.ifcClassesToDisplay;
export const selectPixPolygonArkMcBimItems = createSelector(
  [selectHttpGetMcBimArkItems, selectInstanceClassesToDisplay],
  (mcBimItems, instanceClasses) => {
    if (mcBimItems == null) {
      return [];
    }
    return filterAndTransformMcBiItemsToPixPolygons(mcBimItems);
  }
);

export const selectPixPolygonMcBimCeilingItems = createSelector(
  [selectHttpMcBimCeilingItems],
  (mcBimItems) => {
    if (mcBimItems == null) {
      return [];
    }
    return filterAndTransformMcBiItemsToPixPolygons(mcBimItems);
  }
);

export const selectPixDevicesAndMcBimSpaces = createSelector(
  [selectMultiplacerDevicesPixPolygons, selectMcBimSpacesPixPolygons],
  (devices, spaces) => {
    return [...spaces, ...devices];
  }
);

const selectActiveModule = (state: IRootState) => state.design.activeModule;
export const selectFinalPixPolygons = createSelector(
  [
    selectMcBimSpacesPixPolygons,
    selectMultiplacerDevicesPixPolygons,
    selectPixPolygonMcBimCeilingItems,
    selectValidationTilesPixPolygon,
    selectDeviceMode,
    selectDeviceOnTheMove,
    selectNonPublishedDevicesAsPixPolygons,
    selectPixPolygonArkMcBimItems,
    selectDragSelectSpaces,
    selectActiveModule,
  ],
  (
    spaces,
    devices,
    ceilingMcBimItems,
    spacePixValidations,
    deviceMode,
    deviceOnTheMove,
    nonPublishedDevices,
    pixPolygonArkMcBimItems,
    dragSelectSpaces,
    activeModule
  ): IPixPolygon[] => {
    if (dragSelectSpaces) {
      return [...spaces];
    }

    if (activeModule === "RFP") {
      return [...spaces, ...pixPolygonArkMcBimItems];
    }

    if (activeModule === "CEILING") {
      if (deviceMode === "devices") {
        if (deviceOnTheMove == null) {
          return [...spaces, ...pixPolygonArkMcBimItems, ...devices, ...nonPublishedDevices];
        } else {
          console.log(spacePixValidations);
          return [...spaces, ...pixPolygonArkMcBimItems, ...spacePixValidations, ...devices];
        }
      }
      if (deviceMode === "ifc") {
        return [...spaces, ...pixPolygonArkMcBimItems, ...ceilingMcBimItems];
      }
    }

    return [...spaces, ...pixPolygonArkMcBimItems];
  }
);

export const selectPixActionState = createSelector(
  [selectMeasurementToolActive, selectDragSelectSpaces],
  (measurementToolActive, dragSelectSpaces): PixBimViewerAction => {
    if (measurementToolActive) {
      return "measure";
    } else if (dragSelectSpaces) {
      return "dragSelect";
    } else {
      return "normal";
    }
  }
);

export const selectViewerPathsDeviceDirections = createSelector(
  [selectHttpDevices, selectHideDevices],
  (devices, hideDevices) =>
    getViewerPathAirTerminalDirectionsFromDevices(devices ?? [], hideDevices)
);

export const selectPixPaths = createSelector(
  [selectCeilingGridPixPaths, selectViewerPathsDeviceDirections],
  (ceilingGridPaths, devicePaths) => {
    return [...ceilingGridPaths, ...devicePaths];
  }
);

export const selectFloorExtent = createSelector([selectMcBimSpacesPixPolygons], (spaces) => {
  if (!spaces.length) return null;

  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;

  for (const poly of spaces) {
    for (const pt of poly.points ?? []) {
      const x = pt[0];
      const y = pt[1];
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
  }

  if (
    !Number.isFinite(minX) ||
    !Number.isFinite(minY) ||
    !Number.isFinite(maxX) ||
    !Number.isFinite(maxY)
  ) {
    return null;
  }

  return {
    minX,
    minY,
    maxX,
    maxY,
    width: maxX - minX,
    height: maxY - minY,
  };
});

export const selectFloorExtentKey = createSelector([selectFloorExtent], (ext) => {
  if (!ext) return null;

  const r = (n: number) => Math.round(n * 100) / 100; // 2 desimaler, juster ved behov
  return `${r(ext.minX)}:${r(ext.minY)}:${r(ext.maxX)}:${r(ext.maxY)}`;
});
